<script lang="ts">
    import { closeModal } from 'svelte-modals';
    import { _ } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import { z } from 'zod';

    import ButtonMain from '../../shared/ButtonMain.svelte';
    import ButtonBack from '../../shared/ButtonBack.svelte';
    import InputText from '../../shared/InputText.svelte';
    import Select from '../../shared/Select.svelte';
    import { api } from '../../../stores/api';
    import {
        permissionErrorKey,
        permissionStates,
        UserPermissionsApi,
        type PanelUserType,
        type PermissionStateId,
    } from './UserPermissionsApi';

    export let isOpen: boolean = false;
    /** Wywoływane po udanym dodaniu - lista dokłada wiersz u siebie, bez ponownego pobrania całości. */
    export let onCreated: (user: PanelUserType) => void = () => {};

    let email: string = '';
    let state: PermissionStateId = 'read';
    let error: string = '';
    let saving: boolean = false;

    // pusty adres blokuje przycisk, format sprawdzamy dopiero przy zapisie - inaczej komunikat
    // o błędnym adresie migałby już po wpisaniu pierwszej litery
    $: canSave = email.trim() !== '' && !saving;

    function isEmailValid(address: string): boolean {
        return z.string().email().safeParse(address).success;
    }

    /** Select oddaje sam identyfikator; zwracamy true, bo wybór roli nie może się nie udać. */
    function chooseState(value: string): boolean {
        state = value as PermissionStateId;

        return true;
    }

    /**
     * Guard `canSave` siedzi w handlerze, nie tylko na atrybucie disabled: dwuklik w trakcie zapisu
     * ma nie wysłać drugiego żądania nawet wtedy, gdy zdarzenie przyjdzie z pominięciem przycisku.
     */
    async function save(): Promise<void> {
        if (!canSave) {
            return;
        }

        const address = email.trim();
        if (!isEmailValid(address)) {
            error = $_('Enter a valid e-mail address');

            return;
        }

        saving = true;
        error = '';
        try {
            const user = await UserPermissionsApi.createUser(get(api), address, state);
            saving = false;
            onCreated(user);
            closeModal();
        } catch (e) {
            // modal zostaje otwarty z wpisanymi danymi - odmowę backendu zwykle da się poprawić
            error = $_(permissionErrorKey(e));
            saving = false;
        }
    }
</script>

{#if isOpen}
    <div role="dialog" class="modal">
        <div class="contents">
            <h2 style="text-align: center;">{$_('Add user')}</h2>

            <!-- etykiety są zwykłymi div-ami: <label> wokół komponentu nie przechodzi kontroli
                 a11y Svelte'a, bo kompilator nie widzi kontrolki ukrytej w InputText/Select -->
            <div class="field">
                <div class="label">{$_('E-mail address')}</div>
                <InputText
                    bind:value={email}
                    className="add-user-email"
                    disabled={saving}
                    valid={error === ''}
                    style="width: 100%; border: 1px solid #ccc;"
                />
            </div>

            <div class="field">
                <div class="label">{$_('Initial permissions')}</div>
                <Select
                    items={permissionStates}
                    selected={state}
                    withBorder={true}
                    disabled={saving}
                    width="100%"
                    onChange={chooseState}
                />
            </div>

            {#if error}
                <div class="error">{error}</div>
            {/if}

            <div class="actions">
                <ButtonBack text={$_('Cancel')} showIcon={false} disabled={saving} on:click={closeModal} />
                <ButtonMain text={$_('Add')} showIcon={false} disabled={!canSave} on:click={save} />
            </div>
        </div>
    </div>
{/if}

<style>
    .modal {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1002;

        /* allow click-through to backdrop */
        pointer-events: none;
    }

    .contents {
        width: 390px;
        border-radius: 6px;
        background: white;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        pointer-events: auto;
        font-size: 14px;
        padding: 30px;
        border: 3px solid var(--automapa-blue);
        box-shadow:
            0 0 0 10px #1c5dc722,
            0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .field {
        display: flex;
        flex-direction: column;
        row-gap: 4px;
        margin-top: 16px;
    }

    .field .label {
        opacity: 0.6;
    }

    .error {
        margin-top: 16px;
        color: var(--automapa-red);
    }

    .actions {
        margin-top: 32px;
        display: flex;
        justify-content: center;
        column-gap: 10px;
    }
</style>
